import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { IBrand, IBrandResponse } from '@boma/shared/entities';
import {
  DefinitionTarget,
  ICategory,
  IDefinition,
  IDepartmentResponse,
} from '@boma/supplier-portal/shared/entities';
import {
  BrandsService,
  DefinitionsService,
  HelperFunctionsService,
  SearchService,
} from '@boma/supplier-portal/shared/services';
import { getSelectedSupplierId } from '@boma/supplier-portal/shared/state';
import { Store } from '@ngrx/store';
import { MenuItem, MessageService, TreeNode } from 'primeng/api';
import { Observable, Subscription } from 'rxjs';

// TODO SAVE THE DEFINITIONS INTO STATE SO WE DONT HAVE TO CONTINUE FETCHING IT ALL THE TIME
@Component({
  selector: 'boma-definition-selector',
  templateUrl: './boma-definition-selector.component.html',
  styleUrls: ['./boma-definition-selector.component.scss'],
})
export class BomaDefinitionSelectorComponent implements OnInit {
  @Input() selectedTarget = 7; //   Customer = 1, Product = 2, Location = 4,
  @Input() productOwner = false;
  @Input() selectedDepartment: any = null;
  @Input() selectedCategory: any = null;
  @Input() selectedBrand: any = null;
  @Input() cols: any[] = [
    { field: 'value', header: 'Title' },
    { field: 'type', header: 'Type' },
    { field: 'isSelectable', header: 'Selectable' },
  ];
  @Input() contextName = 'Item';
  @Input() reporting = true; // pass this value as true when we are only using this to filter with, this will show all brands instead of just my brands atm.
  @Input() showHeader = false;
  @Input() showLabels = true;
  @Input() verticalLayout = true;
  @Input() targetId: string; // the id of the location, product or customer we are attaching definitions to.

  @Input() singleSelection = true;
  @Input() editing = true;
  @Input() saving = false;
  @Input() placeholder = 'All';
  @Input() showMenu = false; // not being used

  @Output() selectedItem: EventEmitter<any> = new EventEmitter();
  @Output() selectDepartments: EventEmitter<any> = new EventEmitter();
  @Output() selectCategories: EventEmitter<any> = new EventEmitter();
  @Output() selectBrands: EventEmitter<any> = new EventEmitter();
  @Output() saveProduct: EventEmitter<any> = new EventEmitter();

  selectedTargetName = DefinitionTarget[this.selectedTarget];

  entityId$: Observable<string>;
  entityId: string;
  entityIdSubscription: Subscription;
  loading: boolean;
  complexDefinitions = false;
  maximumAllowedParents = 5;
  maximumAllowedChildren = 100;
  definitionsBeingCreated: string[] = [];

  selectedCategories: ICategory[] = [];
  selectedBrands: IBrand[] = [];

  defaultsChecked = false;

  currentDepartments: IDefinition;
  departmentOptions: IDepartmentResponse[] = [];
  selectedDepartmentOptions: {
    name: string;
    id: string;
  }[] = [];
  currentDefinitions: { [key: string]: IDefinition[] } = {};

  selectedBrandsOptions: {
    name: string;
    id: string;
  }[] = [];

  //might have to update the types on these categories
  currentCategories: IDefinition; //overlap with selected categories?
  categoryOptions: ICategory[] = [];
  selectedCategoryOptions: {
    name: string;
    id: string;
  }[] = [];

  currentBrands: IDefinition;
  brandOptions: IBrandResponse[] = [];

  brands: any = [];
  createFormGroup: FormGroup = new FormGroup({
    value: new FormControl('', Validators.required),
    isSelectable: new FormControl(true, Validators.required),
  });
  definitions: TreeNode[];
  definitionIdTree: { label: string; id: string }[] = [
    { label: 'All', id: '' },
  ];

  items: MenuItem[];
  originalResponse: any[];
  loaded = false;
  awaitingResponse = false;

  definitionTypeOptions: { label: string; value: string }[];
  selectedNode: TreeNode;
  selectedDefinition: IDefinition;
  linkedDefinitions: IDefinition[] = [];

  departmentsLoaded = false;
  categoriesLoaded = false;
  brandsLoaded = false;

  // TODO On close of the multiselected filter the selected options to the top, for easier finding?
  constructor(
    private definitionsService: DefinitionsService,
    private store: Store,
    private searchService: SearchService,
    private messageService: MessageService,
    private brandService: BrandsService,
    private helperService: HelperFunctionsService
  ) {}

  ngOnInit(): void {
    this.selectedTargetName = DefinitionTarget[this.selectedTarget];

    this.definitionTypeOptions = Object.keys(DefinitionTarget)
      .filter((e) => isNaN(Number(e)))
      .map((key) => ({
        label: key,
        value: DefinitionTarget[key],
      }));

    this.items = [
      {
        label: 'Location Groupings',
        id: '4',
        command: (event) => {
          this.getDefinitions(4, 'Location');
        },
      },
      {
        label: 'Product Groupings',
        id: '2',
        command: (event) => {
          this.getDefinitions(2, 'Product');
        },
      },
      {
        label: 'Customer Groupings',
        id: '1',
        command: (event) => {
          this.getDefinitions(1, 'Customer');
        },
      },
    ];

    this.entityId$ = this.store.select(getSelectedSupplierId);
    this.entityIdSubscription = this.entityId$.subscribe(
      (id) => (this.entityId = id)
    );

    this.definitionsService
      .getBrands(this.entityId)
      .subscribe((x) => (this.brands = x));

    this.searchService.getDepartments('').subscribe((result) => {
      this.departmentOptions = result;
      this.getDefinitions();
    });

    //When we create we show only the suppliers brands
    if (this.reporting) {
      this.brandService
        .getAllBrandsUsedInMyCatalogue(this.entityId)
        .subscribe((result) => {
          this.brandOptions = result;
          this.getDefinitions();
        });
    } else {
      this.brandService
        .listBrands(this.entityId, '', '1', '500')
        .subscribe((result) => {
          this.brandOptions = result.items;
          this.getDefinitions();
        });
    }
  }

  getDefinitions(id = null, name = null) {
    if (id && name) {
      this.selectedTarget = id;
      this.selectedTargetName = name;
    }
    //Get the definitions we can choose from
    this.selectedDefinition = null;
    this.definitionIdTree.length = 1;
    this.definitionsService
      .getDefinitions(
        this.entityId, // entity id
        this.selectedTarget, // definition target
        this.selectedDefinition?.id || ''
      )
      .subscribe((payload) => {
        this.mapTreeNodes(payload);
        this.loaded = true;
      });

    // Get the definitions assigned to the current targetId
    if (this.targetId) {
      this.definitionsService
        .getDefinitionsForTarget(this.entityId, this.targetId)
        .subscribe((d) => {
          Object.values(d).forEach((p) => {
            if (!this.currentDefinitions[p.parentId]) return;
            this.currentDefinitions[p.parentId].push(p);
          });
          this.updateDefinitionSelection(this.currentDefinitions);
        });
    }
  }

  checkForDefaultDefinitions(ev) {
    this.selectedDepartmentOptions = [];
    const departmentDefinition = ev.filter(
      (x) => x.value.toLowerCase() === 'department'
    )[0];

    //TODO THINK IF THIS IS REQUIRED FOR SELECTABLE ONLY OPTION
    if (departmentDefinition) {
      departmentDefinition.children = departmentDefinition.children.filter(
        (x) => x.value && x.isSelectable
      );
      this.currentDepartments = departmentDefinition;
      this.departmentOptions.forEach((d) => {
        //push the id of the department to the definition linkId
        this.currentDepartments.children.map((x) => {
          // basically the department array
          if (x.value === d.name) {
            if (x.isSelectable) {
              this.selectedDepartmentOptions.push({
                name: d.name,
                id: d.id,
              });
            }
            x.linkId = d.id;
            return x;
          }
        });
      });
      this.departmentsLoaded = true;
    }

    this.selectedCategoryOptions = [];
    const categoryDefinitions = ev.filter(
      (x) => x.value.toLowerCase() === 'categories'
    )[0];

    if (categoryDefinitions) {
      categoryDefinitions.children = categoryDefinitions.children.filter(
        (x) => x.value && x.isSelectable
      );

      categoryDefinitions.children.map((x) => {
        this.selectedCategoryOptions.push({
          name: x.value,
          id: x.linkId,
        });
      });

      this.currentCategories = categoryDefinitions;
      this.categoriesLoaded = true;
    }

    //TODO Could reduce this code same same everywhere
    this.selectedBrandsOptions = [];
    if (this.reporting) {
      this.brandOptions.map((b) => {
        this.selectedBrandsOptions.push({
          name: b.name,
          id: b.id,
        });
      });
    } else {
      this.currentBrands = ev.filter(
        (x) => x.value.toLowerCase() === 'brands'
      )[0];

      if (this.currentBrands) {
        this.currentBrands.children = this.filterSelectableResults(
          this.currentBrands
        );
        this.brandOptions.forEach((b) => {
          this.currentBrands.children.map((x) => {
            if (x.value === b.name) {
              if (x.isSelectable) {
                this.selectedBrandsOptions.push({
                  name: b.name,
                  id: b.id,
                });
              }
            }
          });
        });

        //We dont need selectedbrandoptions

        this.brandsLoaded = true;
      }
    }

    this.defaultsChecked = true;
    // END OF DEPARTMENT DEFINITIONS
  }

  filterSelectableResults(data) {
    return data.children.filter((x) => x.value && x.isSelectable);
  }

  mapTreeNodes(payload) {
    if (this.selectedTarget === DefinitionTarget.Product)
      this.checkForDefaultDefinitions(payload);

    this.originalResponse = payload;
    //create our current ngmodel array
    payload.map((x) => {
      this.currentDefinitions[x.id] = [];
    });
    this.definitions = payload.map((x) => {
      return {
        data: x,
        children: x.children.map((y) => ({
          data: { ...y },
        })),
      } as TreeNode;
    });
  }

  linkDefinition(rowData) {
    const definitionIndex = this.linkedDefinitions.findIndex(
      (x) => x.id === rowData.id
    );

    if (definitionIndex === -1) {
      this.linkedDefinitions.push(rowData);
    } else {
      this.linkedDefinitions.splice(definitionIndex, 1);
    }

    this.selectedItem.emit({
      definitions: this.helperService.removeDuplicatesAndNullsFromArray(
        this.linkedDefinitions
      ),
      target: this.selectedTargetName,
    });
  }

  nodeSelect(event) {
    if (!event.node.data.children) {
      this.loadChildDefinitions(event.node.data);
    }
  }

  updateDefinitionSelection(ev, parentId = null) {
    if (parentId) this.currentDefinitions[parentId] = ev;
    this.linkedDefinitions = [];
    Object.keys(this.currentDefinitions).forEach((x) => {
      this.currentDefinitions[x].map((y) => {
        this.linkedDefinitions.push(y);
      });
    });

    this.selectedItem.emit({
      definitions: this.helperService.removeDuplicatesAndNullsFromArray(
        this.linkedDefinitions
      ),
      target: this.selectedTargetName,
    });
  }

  loadChildDefinitions(selectedNode, index = null) {
    this.definitionsService
      .getDefinitions(
        this.entityId,
        this.selectedTarget,
        selectedNode.id || selectedNode
      )
      .subscribe((x) => {
        if (selectedNode && !index) {
          this.definitionIdTree.push({
            label: selectedNode.value,
            id: selectedNode.id,
          });
        } else {
          // Shorten to the selected Node
          this.definitionIdTree.length = index + 1;
        }

        // TODO append to the tree rather than replace it
        this.mapTreeNodes(x);
      });
  }

  handleBrands(event) {
    console.log('HANDLE BRANDS', event);
    this.selectBrands.emit(this.selectedBrands);
  }

  handleCategories(ev) {
    this.selectedCategories = ev;
    this.selectCategories.emit(this.selectedCategories);
  }

  handleDefinitionCategoryChange(ev) {
    console.log(ev);
    if (ev.value?.name)
      this.selectedCategory = {
        name: ev.value.name,
        id: ev.value?.id,
      };

    this.selectCategories.emit([this.selectedCategory]);
  }

  handleDepartmentChange(ev) {
    console.log('selected department....', ev);
    this.selectedDepartment = ev;
    this.selectDepartments.emit(this.selectedDepartment);
  }

  handleDefinitionDepartmentChange(ev) {
    this.selectedDepartment = {
      name: ev?.value?.name,
      id: ev.value?.id || null,
    };
    this.selectDepartments.emit(this.selectedDepartment);
  }

  handleDefinitionBrandChange(ev) {
    if (!ev?.value?.name || !ev?.value?.id) return;

    this.selectedBrand = {
      name: ev.value?.name,
      id: ev.value?.id || null,
    };
    this.selectBrands.emit([this.selectedBrand]);
  }

  // Moving attaching directly into this component for simplicity and less duplicated work, can be brought into the individual creates later on.
  linkDefinitionsToTarget() {
    if (!this.targetId) {
      this.messageService.add({
        severity: 'error',
        summary: 'No Grouping Specified',
        detail: 'There was no specified grouping scope selected',
      });
      return;
    }

    if (
      this.selectedDepartment ||
      this.selectedCategory ||
      this.selectedBrand ||
      this.linkedDefinitions.length
    ) {
      if (this.linkedDefinitions.length) {
        const definitionsArray = this.linkedDefinitions.map((x) => x?.id);
        this.definitionsService
          .attachDefinitions(
            this.targetId, // target id
            this.entityId, // entity id
            this.selectedTarget, // definition target
            definitionsArray
          )
          .subscribe((payload) => {
            //need to emit success so we can tell the product edit to close.
          });
      }
      if (
        this.selectedDepartment ||
        this.selectedCategory ||
        this.selectedBrand
      ) {
        // dispatch product save... one time
        this.saveProduct.emit();
      }
    }
  }
}
